import _defineProperty from "/vercel/path1/node_modules/next/dist/compiled/@babel/runtime/helpers/esm/defineProperty.js";
import _toConsumableArray from "/vercel/path1/node_modules/next/dist/compiled/@babel/runtime/helpers/esm/toConsumableArray.js";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import { useState, useEffect, useMemo, useCallback } from "react";
import { isEqual } from "lodash";
import { t } from "i18next";
import { useAuth } from "@qure/qure-auth";
import { useSelectedPatientStore } from "@/stores/selected-patient";
import { usePatientReports, useApplyReportTag } from "@/hooks/report";
import { usePatientAppointments } from "@/hooks/appointments";
import { usePatientEncountersInfinite } from "@/hooks/encounters";
import { useWorkspaceCustomFields } from "@/hooks/custom-fields";
import { usePatientTagsStore } from "@/stores/patient-tags";
import { mapCustomTagsToAITags } from "@/utils/tags";
import { ResourceTab } from "./types";
/** Hook backing the LinkPatientRecordsModal — manages tab state, selection, and data fetching. */
export function useLinkPatientRecordsModal(_ref) {
  var _userInfo$default_wor, _linkedResources$repo, _linkedResources$appo, _linkedResources$enco, _encountersQuery$data3, _encountersQuery$data4;
  var onClose = _ref.onClose,
    onLink = _ref.onLink,
    linkedRes = _ref.linkedRes,
    show = _ref.show,
    initialTab = _ref.initialTab;
  var _useState = useState(ResourceTab.REPORTS),
    activeTab = _useState[0],
    setActiveTab = _useState[1];
  var _useState2 = useState(linkedRes),
    linkedResources = _useState2[0],
    setLinkedResources = _useState2[1];
  useEffect(function () {
    setLinkedResources(linkedRes);
  }, [linkedRes]);
  useEffect(function () {
    if (show) setActiveTab(initialTab !== null && initialTab !== void 0 ? initialTab : ResourceTab.REPORTS);
  }, [show, initialTab]);
  var _useAuth = useAuth(),
    userInfo = _useAuth.userInfo;
  var workspaceId = (_userInfo$default_wor = userInfo === null || userInfo === void 0 ? void 0 : userInfo.default_workspace) !== null && _userInfo$default_wor !== void 0 ? _userInfo$default_wor : null;
  var patientUid = useSelectedPatientStore(function (s) {
    var _s$selectedPatient;
    return ((_s$selectedPatient = s.selectedPatient) === null || _s$selectedPatient === void 0 ? void 0 : _s$selectedPatient.patient_uid) != null ? Number(s.selectedPatient.patient_uid) : null;
  });
  var _useApplyReportTag = useApplyReportTag(),
    applyTag = _useApplyReportTag.applyTag,
    applyingTagId = _useApplyReportTag.applyingTagId;
  var onApplyTag = useCallback(function (tag) {
    var _tag$tagId;
    return applyTag({
      tag_id: (_tag$tagId = tag.tagId) !== null && _tag$tagId !== void 0 ? _tag$tagId : Number(tag.id),
      response: tag.response
    });
  }, [applyTag]);
  var enabled = Boolean(workspaceId && patientUid);
  var _usePatientReports = usePatientReports(patientUid !== null && patientUid !== void 0 ? patientUid : 0, enabled),
    _usePatientReports$da = _usePatientReports.data,
    reports = _usePatientReports$da === void 0 ? [] : _usePatientReports$da;
  var _useWorkspaceCustomFi = useWorkspaceCustomFields(workspaceId, enabled),
    _useWorkspaceCustomFi2 = _useWorkspaceCustomFi.data,
    customFields = _useWorkspaceCustomFi2 === void 0 ? [] : _useWorkspaceCustomFi2;
  var patientTags = usePatientTagsStore(function (s) {
    return s.serverTags;
  });
  var _usePatientAppointmen = usePatientAppointments(workspaceId !== null && workspaceId !== void 0 ? workspaceId : 0, patientUid !== null && patientUid !== void 0 ? patientUid : 0),
    appointmentsData = _usePatientAppointmen.data;
  var encountersQuery = usePatientEncountersInfinite(workspaceId !== null && workspaceId !== void 0 ? workspaceId : 0, patientUid !== null && patientUid !== void 0 ? patientUid : 0);
  var appointments = useMemo(function () {
    var _appointmentsData$tod, _appointmentsData$fut, _appointmentsData$pas;
    if (!appointmentsData) return [];
    return [].concat(_toConsumableArray((_appointmentsData$tod = appointmentsData.today) !== null && _appointmentsData$tod !== void 0 ? _appointmentsData$tod : []), _toConsumableArray((_appointmentsData$fut = appointmentsData.future) !== null && _appointmentsData$fut !== void 0 ? _appointmentsData$fut : []), _toConsumableArray((_appointmentsData$pas = appointmentsData.past) !== null && _appointmentsData$pas !== void 0 ? _appointmentsData$pas : []));
  }, [appointmentsData]);
  var allEncounters = useMemo(function () {
    var _encountersQuery$data, _encountersQuery$data2;
    return (_encountersQuery$data = (_encountersQuery$data2 = encountersQuery.data) === null || _encountersQuery$data2 === void 0 ? void 0 : _encountersQuery$data2.pages.flatMap(function (p) {
      return p.encounters;
    })) !== null && _encountersQuery$data !== void 0 ? _encountersQuery$data : [];
  }, [encountersQuery.data]);
  var isLinkDisabled = useMemo(function () {
    return isEqual(linkedRes, linkedResources);
  }, [linkedResources, linkedRes]);
  var reportsWithTags = useMemo(function () {
    return reports.map(function (r) {
      return _objectSpread(_objectSpread({}, r), {}, {
        versions: r.versions.map(function (v) {
          return _objectSpread(_objectSpread({}, v), {}, {
            aiTags: mapCustomTagsToAITags(v.custom_tags, customFields, patientTags)
          });
        })
      });
    });
  }, [reports, customFields, patientTags]);
  var tabs = [{
    id: ResourceTab.REPORTS,
    label: t("reports") || "Reports",
    count: reports === null || reports === void 0 ? void 0 : reports.length
  }, {
    id: ResourceTab.APPOINTMENTS,
    label: t("appointments") || "Appointments",
    count: appointments === null || appointments === void 0 ? void 0 : appointments.length
  }, {
    id: ResourceTab.ENCOUNTERS,
    label: t("encounters") || "Encounters",
    count: allEncounters === null || allEncounters === void 0 ? void 0 : allEncounters.length
  }];
  var onToggleSelection = function onToggleSelection(id, resourceType) {
    var key = resourceType;
    setLinkedResources(function (prev) {
      return _objectSpread(_objectSpread({}, prev), {}, _defineProperty({}, key, prev[key].includes(id) ? prev[key].filter(function (x) {
        return x !== id;
      }) : [].concat(_toConsumableArray(prev[key]), [id])));
    });
  };
  var handleCancel = function handleCancel() {
    setLinkedResources(linkedRes);
    onClose();
  };
  return {
    activeTab: activeTab,
    onTabChange: setActiveTab,
    linkedResources: linkedResources,
    onToggleSelection: onToggleSelection,
    selectedCount: ((_linkedResources$repo = linkedResources.reports) === null || _linkedResources$repo === void 0 ? void 0 : _linkedResources$repo.length) + ((_linkedResources$appo = linkedResources.appointments) === null || _linkedResources$appo === void 0 ? void 0 : _linkedResources$appo.length) + ((_linkedResources$enco = linkedResources.encounters) === null || _linkedResources$enco === void 0 ? void 0 : _linkedResources$enco.length),
    tabs: tabs,
    handleCancel: handleCancel,
    handleLink: function handleLink() {
      return onLink(linkedResources);
    },
    isLinkDisabled: isLinkDisabled,
    reports: reportsWithTags,
    onApplyTag: onApplyTag,
    applyingTagId: applyingTagId != null ? String(applyingTagId) : null,
    appointments: appointments,
    encounters: allEncounters,
    noOfEncounters: (_encountersQuery$data3 = (_encountersQuery$data4 = encountersQuery.data) === null || _encountersQuery$data4 === void 0 || (_encountersQuery$data4 = _encountersQuery$data4.pages[0]) === null || _encountersQuery$data4 === void 0 ? void 0 : _encountersQuery$data4.total_count) !== null && _encountersQuery$data3 !== void 0 ? _encountersQuery$data3 : allEncounters === null || allEncounters === void 0 ? void 0 : allEncounters.length,
    onLoadMoreEncounters: function onLoadMoreEncounters() {
      return encountersQuery.fetchNextPage();
    },
    hasNextEncountersPage: !!encountersQuery.hasNextPage,
    isFetchingNextEncountersPage: encountersQuery.isFetchingNextPage
  };
}